import axios, { AxiosResponse } from 'axios';
import { BACKENDURL, COUNTRIES_API_KEY, COUNTRIES_BASE_URL } from './constants';
import { userDispatch } from './localStorage';
import storageService from '../../../../Utils/storageService';
import { KEY_ACCESS_TOKEN, KEY_AZURE_TOKEN, KEY_REFRESH_TOKEN } from '../../../../Helpers/constant';
import { config } from '../../../../config/environment';

// Without a timeout, axios waits forever on a stalled connection - reported as
// "Book Facility keeps loading, never navigates" on Android, on multiple devices.
// Every call site already has a .catch() that resets its loading state and shows
// an error, but that only runs once the request actually settles.
// Confirmed live on an Android emulator: the facility/rooms fetch this button
// triggers genuinely took ~25-30s to resolve on a cold connection (not infinite -
// it did eventually render) - and capacitor.config.ts documents this backend
// leaving iOS sockets idle for 79-82s in an earlier investigation. So 30s would
// cut off real, if slow, successful responses; 60s gives real requests room to
// finish while still guaranteeing a genuinely stuck one eventually errors out
// instead of hanging forever.
const REQUEST_TIMEOUT_MS = 60000;

export const AppInstance = axios.create({
  baseURL: BACKENDURL,
  timeout: REQUEST_TIMEOUT_MS,
});

export const AuthInstance = axios.create({
  baseURL: `${BACKENDURL}`,
  timeout: REQUEST_TIMEOUT_MS,
});

// Attach access token to every request (backend expects `access-token`).
const attachAuthInterceptors = (instance: typeof AppInstance) => {
  instance.interceptors.request.use(async (req: any) => {
    // VenuePortal stores tokens under `token` key; SM stores also `KEY_ACCESS_TOKEN`.
    const venueToken: any = await userDispatch({ case: 'GET', key: 'token' });
    const accessToken = venueToken?.accessToken || (await storageService.getItem(KEY_ACCESS_TOKEN));
    if (accessToken) {
      req.headers = req.headers || {};
      req.headers['access-token'] = `${accessToken}`;
      req.headers['Authorization'] = `${accessToken}`;
    }
    return req;
  });
};

attachAuthInterceptors(AppInstance);
attachAuthInterceptors(AuthInstance);

let refreshPromise: Promise<string> | null = null;

const getVenueRefreshToken = async (): Promise<string | null> => {
  const venueToken: any = await userDispatch({ case: 'GET', key: 'token' });
  return venueToken?.refreshToken || (await storageService.getItem(KEY_REFRESH_TOKEN));
};

const saveVenueTokens = async (accessToken: string, refreshToken: string) => {
  const venueToken: any = await userDispatch({ case: 'GET', key: 'token' });
  await userDispatch({
    case: 'ADD',
    key: 'token',
    payload: { ...(venueToken || {}), accessToken, refreshToken },
  });
  await storageService.setItem(KEY_ACCESS_TOKEN, accessToken);
  await storageService.setItem(KEY_REFRESH_TOKEN, refreshToken);
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = await getVenueRefreshToken();
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }
  const azureToken = await storageService.getItem(KEY_AZURE_TOKEN);
  const res = await axios.post(
    config.refreshUrl,
    { refreshToken },
    azureToken
      ? {
          headers: {
            Authorization: `Bearer ${azureToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      : undefined
  );

  const { accessToken, refreshToken: newRefreshToken } = res?.data || {};
  const nextAccess = accessToken?.token || accessToken;
  const nextRefresh = newRefreshToken?.token || newRefreshToken;

  if (!nextAccess || !nextRefresh) {
    throw new Error('Refresh response missing tokens');
  }

  await saveVenueTokens(nextAccess, nextRefresh);
  return nextAccess;
};

const attachRefreshInterceptor = (instance: typeof AppInstance) => {
  instance.interceptors.response.use(
    (resp: any) => resp,
    async (err: any) => {
      const status = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      const originalRequest = err?.config;

      if (!originalRequest || (status !== 401 && errorCode !== 'invalid_access_token')) {
        return Promise.reject(err);
      }

      if (originalRequest._retry) {
        return Promise.reject(err);
      }
      originalRequest._retry = true;

      // A guest never has a refresh token in the first place (they only ever
      // get local `user`/`role` flags, not a real backend session) - that's
      // not an expired session to recover from, so don't treat it like one.
      // Only a real logged-in user whose refresh token exists and then fails
      // to redeem it has actually had their session expire.
      const hadRefreshToken = !!(await getVenueRefreshToken());
      if (!hadRefreshToken) {
        return Promise.reject(err);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccess = await refreshPromise;

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `${newAccess}`;
        originalRequest.headers['access-token'] = `${newAccess}`;

        return instance(originalRequest);
      } catch (refreshErr) {
        await userDispatch({ case: 'CLEAR' });
        await storageService.clear();
        window.location.href = '/login';
        return Promise.reject(refreshErr);
      }
    }
  );
};

attachRefreshInterceptor(AppInstance);
attachRefreshInterceptor(AuthInstance);

/*
*
*
Auth API's
*
*
*/

export const getOtp: (email: string) => Promise<AxiosResponse<any>> = async (email: string) => {
  const path = `/v1/auth/send/otp`;
  const response: any = await AuthInstance.post(path, {}, { params: { email: email } });
  return response;
};

export const verifyOtp: (email: string, otp: string) => Promise<AxiosResponse<any>> = async (email: string, otp: string) => {
  const path = `/v1/auth/verify/otp`;
  const response: any = await AuthInstance.get(path, {
    params: { email: email, otp: otp },
  });
  return response;
};

export const userLogin: (payload: any) => Promise<AxiosResponse<any>> = async (payload: any) => {
  const path = '/v1/auth/login/verify';
  const response: any = await AuthInstance.post(path, payload);
  return response;
};

export const updatePassword: (payload: any) => Promise<AxiosResponse<any>> = async (payload: any) => {
  const path = '/v1/auth/update/password';
  const response: any = await AuthInstance.post(path, payload);
  return response;
};

export const getToken: (payload: any) => Promise<AxiosResponse<any>> = async (payload: any) => {
  const path = '/v1/helper/getToken';
  const response: any = await AuthInstance.get(path, payload);
  return response;
};

/*
*
*
Application API's
*
*
* */

export const getFacilities: () => Promise<AxiosResponse<any>> = async () => {
  const path = '/v1/facility/get/facility';
  const response = AppInstance.get(path);
  return response;
};

export const getFacilityBookings: (id: string) => Promise<AxiosResponse<any>> = async (id: string) => {
  const path = `/v1/facility/get/facility/booking`;
  const response: any = AppInstance.get(path, { params: { id } });
  return response;
};

export const getFacilityDetails: (id: string) => Promise<AxiosResponse<any>> = async (id: string) => {
  const path = `/v1/facility/get/facility/ById`;
  const response = await AppInstance.get(path, { params: { id } });
  return response;
};

export const getVenueBookingConfiguration: () => Promise<AxiosResponse<any>> = async () => {
  const path = `/v1/helper/ken-configuration/venue-booking`;
  return await AppInstance.get(path);
};

export const updateFacility: (payload: any) => Promise<AxiosResponse<any>> = async (payload: any) => {
  const path = `/v1/facility/update/facility/booking`;
  const response = await AppInstance.post(path, payload);
  return response;
};

export const bookFacility: (payload: any) => Promise<AxiosResponse<any>> = async (payload: any) => {
  const path = `/v1/facility/book/facility`;
  const response = await AppInstance.post(path, payload);
  return response;
};

export const getCouponCodes: (pricingIds: Array<string>, facility?: Array<string>) => Promise<AxiosResponse<any>> = async (pricingIds: Array<string>, facility?: Array<string>) => {
  const user = await storageService.getItem('user');
  const { applicantId: id } = typeof user === 'string' ? JSON.parse(user) : user;
  const path = `v1/facility/get/couponCode?id=${id||user?.id}&pricings=${pricingIds}&facilities=${facility}`;
  const response = await AppInstance.get(path);
  return response;
};

export const generatePayment: (payload: any) => Promise<AxiosResponse<any>> = async (payload: any) => {
  // This creates a brand-new N-Genius order server-side on every call — it must never be served
  // from a cache. On iOS, repeat checkouts (even with a different cart/amount) were observed
  // getting back the exact same, already-expired payment link, landing on N-Genius's own
  // "payment link does not exist" error — the classic symptom of a stale cached response being
  // replayed instead of a fresh network round-trip. The cache-busting query param defeats any
  // cache keyed on path alone (ignoring body), and the headers defeat any that does respect them.
  const path = `/v1/payment/initiate/international/payment?_=${Date.now()}`;
  const response = await AppInstance.post(path, payload, {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    },
  });
  return response;
};

export const getBookings: () => Promise<AxiosResponse<any>> = async () => {
  const user = await storageService.getItem('user');
  const parsed = typeof user === 'string' ? JSON.parse(user) : user;
  const userId = parsed?.applicantId || parsed?.id;
  // Guest has no Salesforce Account/Contact Id — skip invalid SOQL call
  if (!userId || userId === 'guest' || String(userId).toLowerCase() === 'guest') {
    return { data: { status: 'true', result: [] } } as AxiosResponse<any>;
  }
  const path = `/v1/facility/get/facility/booking?accountId=${userId}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getAllBookings: () => Promise<AxiosResponse<any>> = async () => {
  const path = `/v1/facility/get/facility/booking`;
  const response = await AppInstance.get(path);
  return response;
};

export const getExisitngBookings: (id: string) => Promise<AxiosResponse<any>> = async (id: string) => {
  const queryString: Array<string> = ['a0RH4000000QOoGMAW', 'a0RH4000000QOoFMAW', 'a0RH4000000QOpcMAG'];
  let paramsString;
  if (queryString.includes(id)) {
    paramsString = queryString
      .filter((items) => {
        return items !== id;
      })
      .map((id: string) => `subItemId=${encodeURIComponent(id)}`)
      .join('&');
  }
  const path = paramsString ? `/v1/facility/get/facility/booking?itemId=${id}&${paramsString}` : `/v1/facility/get/facility/booking?itemId=${id}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getFacilityDiscounts: (itemId: string) => Promise<AxiosResponse<any>> = async (itemId: string) => {
  const user = await storageService.getItem('user');
  const parsed = typeof user === 'string' ? JSON.parse(user) : user;
  const userId = parsed?.applicantId || parsed?.id;
  if (!userId || userId === 'guest' || String(userId).toLowerCase() === 'guest') {
    return { data: { status: 'true', result: [] } } as AxiosResponse<any>;
  }
  const path = `/v1/facility/get/facility/discount?itemId=${itemId}&accountId=${userId}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getImages: (id: string) => Promise<AxiosResponse<any>> = async (id: string) => {
  const path = `/v1/facility/get/images?id=${id}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getPricing: (roomId: string) => Promise<AxiosResponse<any>> = async (roomId: string) => {
  const path = `/v1/facility/get/facility/pricing?roomId=${roomId}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getPolicyDetails: (id: string) => Promise<AxiosResponse<any>> = async (id: string) => {
  const path = `/v1/facility/get/policy/criterial?id=${id}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getTransactions: () => Promise<AxiosResponse<any>> = async () => {
  const user = await storageService.getItem('user');
  const userData = typeof user === 'string' ? JSON.parse(user) : user;
  const { applicantId: userId } = userData;
  const path = `/v1/transaction/byId?accountId=${userId||userData?.id}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getFacilityHomeRules: () => Promise<AxiosResponse<any>> = async () => {
  const path = `/v1/facility/get/facility/homerules`;
  const response: any = AppInstance.get(path);
  return response;
};

export const facilitiesChecking: (itemId: string, startTime: any, endTime: any, reschedule?: string, timeZone?: string) => Promise<AxiosResponse<any>> = async (itemId: string, startTime: string, endTime: string, reschedule?: string, timeZone?: string) => {
  const path = `/v1/facility/check/facility/booking?itemId=${itemId}&startDateTime=${startTime}&endDateTime=${endTime}&reschedule=${reschedule}${timeZone ? `&timeZone=${encodeURIComponent(timeZone)}` : ''}`;
  const respones = await AppInstance.get(path);
  return respones;
};

export const contactAdmin: (payload: any) => Promise<AxiosResponse<any>> = async (payload: any) => {
  const path = '/v1/facility/create_case';
  const response = await AppInstance.post(path, payload);
  return response;
};

export const getCertificates: () => Promise<AxiosResponse<any>> = async () => {
  const user: any = await userDispatch({ case: 'GET', key: 'user' });
  const accountId = user?.applicantId || user?.id;
  if (!accountId) {
    // Avoid calling backend with `accountId=undefined`
    throw new Error('Missing accountId');
  }
  const path = `/v1/facility/get/facility/certificate`;
  return await AppInstance.get(path, { params: { accountId } });
};

export const thirdPartyBooking: (payload: any) => Promise<AxiosResponse> = async (payload: any) => {
  const path = '/v1/facility/book/thirdparty';
  const response = await AppInstance.post(path, payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'multipart/form-data',
    },
  });
  return response;
};

export const memberShipOnboard: (payload: any) => Promise<AxiosResponse> = async (payload: any) => {
  const path = '/v1/payment/membership/initiate/international/payment';
  const response = await AppInstance.post(path, payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'multipart/form-data',
    },
  });
  return response;
};

export const cancelRequest: (payload: any) => Promise<AxiosResponse> = async (payload: any) => {
  const path = '/v1/facility/request/cancellation';
  const response = await AppInstance.post(path, payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'multipart/form-data',
    },
  });
  return response;
};

export const getLiabilityForm: () => Promise<AxiosResponse<any>> = async () => {
  const path = '/v1/facility/get/liabilityform';
  const respones = await AppInstance.get(path);
  return respones;
};
export const postThirdPartyFileUpload: (payload: any) => Promise<AxiosResponse<any>> = async (payload) => {
  const path = `/v1/facility/fileupload/thirdparty`;
  const response = await AppInstance.post(path, payload);
  return response;
};

export const updateThirdPartyFileUploadStatus: (payload: any) => Promise<AxiosResponse<any>> = async (payload) => {
  const path = `/v1/facility/update/fileupload/status`;
  const response = await AppInstance.post(path, payload);
  return response;
};

export const postAccessCardRequest: (payload: any) => Promise<AxiosResponse<any>> = async (payload) => {
  const path = `/v1/facility/account/${payload.accountId}`;
  const response = await AppInstance.patch(path, payload);
  return response;
};

export const getLiabilityPolicy: (payload: any) => Promise<AxiosResponse<any>> = async (id) => {
  const path = `/v1/facility/get/liability/policy/${id}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getBookingQuestions: (facilityId: string) => Promise<AxiosResponse<any>> = async (facilityId: string) => {
  const path = `/v1/facility/get/booking/questions`;
  const response = await AppInstance.get(path, { params: { facilityId } });
  return response;
};
export const deleteCartItems: (bookingId: string) => Promise<AxiosResponse<any>> = async (bookingId) => {
  const path = '/v1/facility/delete/cart';
  const respones = await AppInstance.get(path, {
    params: {
      bookingId: bookingId,
    },
  });
  return respones;
};

export const updateLiabilityForm: (payload: any) => Promise<AxiosResponse> = async (payload: any) => {
  const path = '/v1/facility/update/liabilityform';
  const response = await AppInstance.post(path, payload);
  return response;
};

export const getUserDetails: () => Promise<AxiosResponse<any>> = async () => {
  const user = await storageService.getItem('user');
  const { applicantId: userId } = typeof user === 'string' ? JSON.parse(user) : user;
  const path = `/v1/auth/userdetails?id=${userId||user?.id}`;
  const response = await AppInstance.get(path);
  return response;
};

export const getDropDownDatas: (data: any) => Promise<AxiosResponse<any>> = async (data) => {
  const path = `/v1/helper/getDropDownValues?objectName=${data.objectName}&fieldName=${data.fieldName}`;
  const response = await AppInstance.get(path);
  return response;
};

export const postBookingFileUpload: (payload: any) => Promise<AxiosResponse<any>> = async (payload) => {
  const path = `/v1/facility/fileupload/booking`;
  const response = await AppInstance.post(path, payload);
  return response;
};

const axiosinstancecountries = axios.create({
  baseURL: COUNTRIES_BASE_URL,
  headers: {
    accept: 'application/json',
    'X-CSCAPI-KEY': COUNTRIES_API_KEY,
  },
});

export const getCountries = async () => {
  const path = 'v1/countries';
  const res = await axiosinstancecountries.get(path);
  return res && res.data ? res.data : null;
};
